<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ArrowTopRightOnSquareIcon, Squares2X2Icon, XMarkIcon } from '@heroicons/vue/24/outline'
import { appShortcuts, filterAppShortcuts, type AppShortcut } from '@/v2/data/appLauncher'
import { HUB_ACCENTS, type HubAccent } from '@/v2/data/sidebarMenu'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

const boostCoreStore = useBoostCorePermissionStore()

/** Raccourcis visibles pour l'utilisateur courant (gating + résolution d'URL). */
const apps = computed(() => filterAppShortcuts(appShortcuts, boostCoreStore))

/** Couleur d'accent d'une tuile (jeton de marque) ; primary par défaut. */
function accentColor(key?: HubAccent): string {
  return key ? HUB_ACCENTS[key].color : 'var(--color-primary)'
}
function tileIconStyle(app: AppShortcut) {
  const c = accentColor(app.accent)
  return {
    color: c,
    backgroundColor: `color-mix(in oklab, ${c} 14%, transparent)`,
  }
}

// ── Ouverture / fermeture via <dialog> natif (Esc + focus-trap + backdrop) ───
const open = ref(false)
const dialogRef = ref<HTMLDialogElement | null>(null)

watch(open, (isOpen) => {
  const dialog = dialogRef.value
  if (!dialog) return
  if (isOpen && !dialog.open) dialog.showModal()
  else if (!isOpen && dialog.open) dialog.close()
})

function onBackdropClick(e: MouseEvent) {
  if (e.target === dialogRef.value) open.value = false
}
</script>

<template>
  <!-- Déclencheur dans le header -->
  <button
    type="button"
    class="btn btn-square btn-ghost"
    aria-label="Lanceur d'applications"
    aria-haspopup="dialog"
    :aria-expanded="open"
    @click="open = true"
  >
    <Squares2X2Icon class="h-5 w-5" aria-hidden="true" />
  </button>

  <!-- Panneau lanceur : <dialog> natif modal -->
  <dialog
    ref="dialogRef"
    class="m-0 mt-16 ml-auto mr-3 w-[min(30rem,calc(100vw-1.5rem))] rounded-box border border-base-300 bg-base-100 p-0 shadow-lg backdrop:bg-black/40 lg:mr-4"
    aria-labelledby="app-launcher-title"
    @click="onBackdropClick"
    @close="open = false"
  >
    <div class="flex max-h-[min(40rem,80dvh)] flex-col">
      <!-- En-tête -->
      <div class="flex items-start justify-between gap-3 border-b border-base-300 px-4 py-3">
        <div class="min-w-0">
          <h2 id="app-launcher-title" class="text-lg font-semibold text-base-content">
            Lanceur d'applications
          </h2>
          <p class="text-sm text-base-content/70">
            {{ apps.length }} application{{ apps.length > 1 ? 's' : '' }}
          </p>
        </div>
        <button
          type="button"
          class="btn btn-square btn-ghost"
          aria-label="Fermer le lanceur"
          @click="open = false"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Grille de raccourcis (liste plate) -->
      <div class="min-h-0 flex-1 overflow-y-auto p-3">
        <ul role="list" class="grid grid-cols-3 gap-1.5">
          <li v-for="app in apps" :key="app.id">
            <!-- Description = popover natif (`title`) + nom accessible (`aria-label`) :
                 aucun élément en overflow, donc pas de scrollbar parasite. -->
            <a
              :href="app.href"
              target="_blank"
              rel="noopener noreferrer"
              :title="app.description"
              :aria-label="`${app.label} — ${app.description} (ouvre dans un nouvel onglet)`"
              class="flex min-h-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-box border border-transparent p-2 text-center transition-colors hover:border-base-300 hover:bg-base-200 focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none"
            >
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-box"
                :style="tileIconStyle(app)"
                aria-hidden="true"
              >
                <component :is="app.icon" class="h-5 w-5" />
              </span>
              <span class="text-sm font-medium leading-tight text-base-content">
                {{ app.label }}
              </span>
            </a>
          </li>
        </ul>

        <p v-if="apps.length === 0" class="py-8 text-center text-sm text-base-content/70">
          Aucune application disponible.
        </p>
      </div>

      <!-- Pied : rappel ouverture externe -->
      <div
        class="flex shrink-0 items-center justify-between gap-2 border-t border-base-300 bg-base-200 px-4 py-2 text-sm text-base-content/70"
      >
        <span>Ouverture dans un nouvel onglet</span>
        <span class="flex items-center gap-1">
          <ArrowTopRightOnSquareIcon class="h-4 w-4" aria-hidden="true" />
          liens externes
        </span>
      </div>
    </div>
  </dialog>
</template>
